/**
 * Morph transitions: elements that appear on consecutive slides glide from
 * their old geometry to the new one (FLIP), the rest crossfades.
 *
 * Matching order (each element is used once):
 *   1. same explicit `morphKey`
 *   2. same element `id` (duplicated slides keep ids)
 *   3. same media source (image / video / histology tiles / 3D model)
 *   4. same plain text
 * If nothing matches, the caller falls back to a standard transition.
 */

import { easingCss, prefersReducedMotion } from "./transitions";
import { getRenderableElements } from "./slideDesign";

const px = (value) => {
  if (typeof value === "number") return value;
  const n = parseFloat(String(value ?? ""));
  return Number.isFinite(n) ? n : 0;
};

const rectOf = (el) => ({
  x: px(el?.position?.left),
  y: px(el?.position?.top),
  w: Math.max(1, px(el?.dimension?.width)),
  h: Math.max(1, px(el?.dimension?.height)),
  r: Number(el?.rotation) || 0,
});

const mediaKey = (el) => {
  if (!el || el.type === "TEXT") return null;
  const c = el.content;
  const url = typeof c === "string" ? c : c?.url || c?.src || null;
  return url ? `${el.type}|${url}` : null;
};

const textKey = (el) => {
  if (!el || el.type !== "TEXT" || typeof el.content !== "string") return null;
  const plain = el.content.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
  return plain ? `TEXT|${plain}` : null;
};

export const matchMorphElements = (fromData, toData) => {
  const from = getRenderableElements(fromData).filter((e) => !e.isBackground && e.id);
  const to = getRenderableElements(toData).filter((e) => !e.isBackground && e.id);
  const used = new Set();
  const pairs = [];

  const pass = (keyFn) => {
    to.forEach((t) => {
      if (pairs.some((p) => p.to === t)) return;
      const k = keyFn(t);
      if (!k) return;
      const f = from.find((candidate) => !used.has(candidate) && keyFn(candidate) === k);
      if (f) {
        used.add(f);
        pairs.push({ from: f, to: t });
      }
    });
  };

  pass((e) => (e.morphKey ? `key|${e.morphKey}` : null));
  pass((e) => `id|${e.id}`);
  pass(mediaKey);
  pass(textKey);
  return pairs;
};

const q = (root, id) => root?.querySelector?.(`[data-el-id="${CSS.escape(String(id))}"]`) || null;

/**
 * Run a morph between two slide layers. Returns null when there is nothing
 * to morph so the caller can use its fallback transition.
 */
export const runMorph = ({ outEl, inEl, fromData, toData, duration = 800, easing = "cinematic" }) => {
  const pairs = matchMorphElements(fromData, toData);
  if (!pairs.length || !inEl || typeof inEl.animate !== "function") return null;
  if (prefersReducedMotion()) return { finished: Promise.resolve(), finish: () => {} };

  const timing = { duration, easing: easingCss(easing), fill: "both" };
  const fadeTiming = { duration: Math.round(duration * 0.7), easing: "ease-out", fill: "both" };
  const animations = [];
  const restore = [];

  const prevInZ = inEl.style.zIndex;
  const prevOutZ = outEl?.style.zIndex;
  inEl.style.zIndex = "2";
  if (outEl) outEl.style.zIndex = "1";
  restore.push(() => {
    inEl.style.zIndex = prevInZ;
    if (outEl) outEl.style.zIndex = prevOutZ;
  });

  const matchedIn = new Set();
  pairs.forEach(({ from, to }) => {
    const nodeIn = q(inEl, to.id);
    if (!nodeIn) return;
    matchedIn.add(nodeIn);
    const a = rectOf(from);
    const b = rectOf(to);
    const dx = a.x + a.w / 2 - (b.x + b.w / 2);
    const dy = a.y + a.h / 2 - (b.y + b.h / 2);
    const sx = a.w / b.w;
    const sy = a.h / b.h;
    const prevOrigin = nodeIn.style.transformOrigin;
    nodeIn.style.transformOrigin = "center center";
    restore.push(() => {
      nodeIn.style.transformOrigin = prevOrigin;
    });
    animations.push(
      nodeIn.animate(
        [
          { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy}) rotate(${a.r}deg)` },
          { transform: `translate(0px, 0px) scale(1, 1) rotate(${b.r}deg)` },
        ],
        timing
      )
    );
    const nodeOut = q(outEl, from.id);
    if (nodeOut && typeof nodeOut.animate === "function") {
      animations.push(nodeOut.animate([{ opacity: 0 }, { opacity: 0 }], timing));
    }
  });

  // Everything that did not morph crossfades.
  inEl.querySelectorAll("[data-cine-backdrop], [data-el-id]").forEach((node) => {
    if (matchedIn.has(node) || typeof node.animate !== "function") return;
    animations.push(node.animate([{ opacity: 0 }, { opacity: 1 }], fadeTiming));
  });
  if (outEl && typeof outEl.animate === "function") {
    animations.push(outEl.animate([{ opacity: 1 }, { opacity: 0 }], timing));
  }

  let settled = false;
  const cleanup = () => {
    if (settled) return;
    settled = true;
    animations.forEach((anim) => {
      try {
        anim.cancel();
      } catch (error) {
        /* ignore */
      }
    });
    restore.forEach((fn) => fn());
  };
  const finished = Promise.all(animations.map((anim) => anim.finished.catch(() => null))).then(cleanup);
  return {
    finished,
    finish: () => {
      animations.forEach((anim) => {
        try {
          anim.finish();
        } catch (error) {
          /* ignore */
        }
      });
      cleanup();
    },
  };
};
